import { map, catchError, switchMap, withLatestFrom, debounceTime, auditTime } from 'rxjs/operators';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { toObservable } from '@angular/core/rxjs-interop';
import { IFilterOperator } from '@aston/foundation';
import { Injectable, inject } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Observable, of, zip } from 'rxjs';

import { AccountingListFilterSpecService } from '../../../accounting-module/services/accounting-list-filter-spec.service';
import { FactorFeaturesStore, observeFeature } from '../../../factor-features-module/stores/factor-features';
import { SuperDebtorPageStore } from '../../../debtors-module/stores/super-debtor-page';
import { SuperDebtorDetailService } from '../../../debtors-module/services';
import { DunningActionOrigin } from '../../../dunnings-module/enums';
import { IDunningAction } from '../../../dunnings-module/models';
import { addAccountingSubTypeSorts } from '../../functions';
import * as AppStoreActions from '../../app-store/actions';
import * as featureSelectors from '../selectors';
import * as featureActions from '../actions';

@Injectable({
	providedIn: 'root'
})
export class DunningActionDetailStoreAccountingEffects {

	private superDebtorDetailService = inject(SuperDebtorDetailService);
	private ffs = inject(FactorFeaturesStore);
	private actions$ = inject(Actions);
	private store = inject(Store);
	private accountingListFilterSpecService = inject(AccountingListFilterSpecService);

	payingCenterModeEnabled$ = toObservable(inject(SuperDebtorPageStore).payingCenterModeEnabled)

	loadAccountingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadAccountingRequest),
		withLatestFrom(
			this.payingCenterModeEnabled$,
			this.store.select(featureSelectors.selectAccountingSlice),
			this.store.select(featureSelectors.selectSuperDebtorId),
			this.store.select(featureSelectors.selectActionsSpecs),
			this.store.select(featureSelectors.selectDunningAction)),
		auditTime(200),
		switchMap(([action, payingCenterMode, accountingDocs, superDebtorId, specs, dunningAction]) => {
			const { filters: otherFilters } = accountingDocs;

			// hard filters
			// https://astonitf.visualstudio.com/LBP/_workitems/edit/10816
			const filters = [
				this.accountingListFilterSpecService.getDunningLevelFieldFilter(action.level),
				...otherFilters,
			];

			if (!specs[dunningAction.type].canIncludeCompletedDocuments(dunningAction as unknown as IDunningAction)) {
				filters.push(this.accountingListFilterSpecService.excludeCompletedDocuments);
			}

			let paging = accountingDocs.paging;

			// If sorting on type, add paymentType as second sort
			paging = {
				...paging,
				sorts: addAccountingSubTypeSorts(paging.sorts)
			};

			return this.superDebtorDetailService
				.getSuperDebtorAccounting(superDebtorId, payingCenterMode, {
					...paging, filters
				}).pipe(
					map(list => featureActions.LoadAccountingSuccess({list, correlationParams: action.correlationParams})),
					catchError(error => of(featureActions.LoadAccountingFailure({error})))
				);
		})
	));

	// Reload data when a UpdateSettingsAction is dispatched
	updateAccountingSettingsEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateAccountingSettings),
		withLatestFrom(this.store.select(featureSelectors.selectDunningAction)),
		map(([_, dunningAction]) => {
			const level = dunningAction.dunningLevel;
			const toRealize = !dunningAction.realizationDate;
			return (level && toRealize) ? featureActions.LoadAccountingRequest(level) : featureActions.LoadSelectedAccountingRequest();
		})
	));

	loadSelectedAccountingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSelectedAccountingRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectAccountingIsLoading),
			this.store.select(featureSelectors.selectAccountingFilters),
			this.store.select(featureSelectors.selectAccountingPaging),
			this.store.select(featureSelectors.selectSuperDebtorId),
			this.payingCenterModeEnabled$),
		debounceTime(100),

		// FIXME: this emits way too much.
		//
		//    wish it could be that simple, but this blocks validation loaders
		//    distinctUntilChanged(([ _, pda], [__, nda]) => pda?.accounting?.length === nda?.accounting?.length),

		switchMap(([action, dunningAction, _, otherFilters, paging, superDebtorId, payingCenterMode]) => {
			const selectedIds = dunningAction.accounting;
			const toRealize = !dunningAction.realizationDate;

			// if none are selected, just dispatch an empty list
			if (!selectedIds || !selectedIds.length) {
				return of(featureActions.LoadSelectedAccountingSuccess({list: {items: []}}));
			}

			const { accountingIdFilterSpec } = this.accountingListFilterSpecService;

			const filters = [...otherFilters, {
				field: accountingIdFilterSpec.field.value, operator: IFilterOperator.Equals, values: selectedIds, spec: accountingIdFilterSpec
			}];

			return zip(
				this.superDebtorDetailService.getSuperDebtorAccounting(superDebtorId, payingCenterMode, {	...paging, filters }),
				this.superDebtorDetailService.getSuperDebtorAccountingTotals(superDebtorId, filters)
			).pipe(
				switchMap(([list, totals]) => [

					featureActions.LoadSelectedAccountingSuccess({
						list: {...list, meta: {totals}},
						correlationParams: action.correlationParams
					}),

					// on a realized dunning action, the accounting list is the selected list
					toRealize ? AppStoreActions.Noop() : featureActions.LoadAccountingSuccess({list})
				]),
				catchError(error => of(featureActions.LoadSelectedAccountingFailure({error})))
			);
		})
	));

	// Reload data when a UpdateSettingsAction is dispatched
	updateSelectedAccountingSettingsEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateAccountingSettings),
		map(_ => featureActions.LoadSelectedAccountingRequest())
	));

	// User Story 39277: [MO][Relance] - Gestion de l'affichage lié aux pièces exclues au sein des actions créées automatiquement
	countExcludedAccountingDocumentsEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CountExcludedAccountingDocumentsRequest, featureActions.LoadSelectedAccountingRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectSuperDebtorId),
			this.store.select(featureSelectors.selectDunningAction),
			observeFeature('isFilteringAccountingDunningEnabled')
		),
		switchMap(([_, superDebtorId, dunningAction, optionEnabled]) => {
			if (!optionEnabled || !dunningAction.id || dunningAction.origin !== DunningActionOrigin.DunningSystem)
				return [featureActions.CountExcludedAccountingDocumentsSuccess({ entity: 0 })]

			const commonFilters = [this.accountingListFilterSpecService.excludeCompletedDocuments, this.accountingListFilterSpecService.byLevel(dunningAction.dunningLevel)]
			const excludedTypes = this.ffs.filteringAccountingDunningData().accountingDocumentTypes || []
			const excludedMethods = this.ffs.filteringAccountingDunningData().paymentTypes || []

			const filtersMatchingTypes = [...commonFilters, this.accountingListFilterSpecService.byType(IFilterOperator.Equals, excludedTypes)]
			const countMatchingTypes$ = excludedTypes.length ? this.superDebtorDetailService.getSuperDebtorAccountingCount(superDebtorId, filtersMatchingTypes) : of(0)

			const filtersMatchingMethods = [...commonFilters, this.accountingListFilterSpecService.byPaymentType(IFilterOperator.Equals, excludedMethods)]
			const countMatchingMethods$ = excludedMethods.length ? this.superDebtorDetailService.getSuperDebtorAccountingCount(superDebtorId, filtersMatchingMethods) : of(0)

			return zip([ countMatchingTypes$, countMatchingMethods$ ]).pipe(
				map(([ countMatchingTypes, countMatchingMethods ]) => countMatchingTypes + countMatchingMethods),
				map(entity => featureActions.CountExcludedAccountingDocumentsSuccess({entity})),
				catchError(error => of(featureActions.CountExcludedAccountingDocumentsFailure({error})))
			)
		})
	))
}
